import Dexie from 'dexie';
import tableConstructor from './idb.table';
import type { IDB, IDBKeyType, IDBOptions, IVersion } from './interface';

const DEFAULT_DB_NAME = 'Workspace-DB';

async function _flatten<DataType extends Record<string, unknown>>(
    dexie: Dexie,
    mappings: Record<string, unknown>,
): Promise<string> {
    const records: Record<string, unknown[]> = {};

    const jobs = Object.keys(mappings).map(async key => {
        const table = dexie.table(key);
        const allItems = (await table.toArray()) as DataType[];
        records[key] = allItems.map(item =>
            typeof item['flatten'] === 'function' ? (item['flatten']() as unknown) : item,
        );
    });

    await Promise.all(jobs);

    const result = JSON.stringify(records);

    return result;
}

async function _apply(
    dexie: Dexie,
    data: string,
    mappings: Record<string, unknown>,
): Promise<void> {
    const records = JSON.parse(data) as Record<string, unknown[]>;

    const jobs = Object.entries(records).map(([tableName, items]) => {
        const table = dexie.table(tableName);
        return table.bulkAdd(items);
    });

    await Promise.all(jobs);
}

function IndexedDB(name: string = DEFAULT_DB_NAME, options?: IDBOptions): IDB {
    const dexieDB = new Dexie(name, {});

    dexieDB.version(options?.version ?? 1).stores(options?.mappings ?? {});
    let openCount = 0;

    const db = {
        dexieDB /* Include a private reference if needed */,

        name,

        version: (versionNumber: number) => dexieDB.version(versionNumber) as unknown as IVersion,

        open: () =>
            new Promise((resolve, reject) => {
                dexieDB
                    .open()
                    .then(() => {
                        openCount += 1;
                        resolve(db);
                    })
                    .catch(error => {
                        reject(error);
                    });
            }),

        table: <DataType extends Record<IDBKeyType, unknown>, IDType extends keyof DataType>(
            tableName: string,
        ) => tableConstructor<DataType, IDType>(dexieDB, tableName),

        close: () => {
            if (openCount === 1) {
                dexieDB.close();
            }

            openCount = Math.max(0, openCount - 1);
            /* No-Op */
        },

        delete: () => dexieDB.delete(),

        isOpen: () => openCount > 0,

        flatten: () => _flatten(dexieDB, options?.mappings ?? {}),

        apply: (data: string) => _apply(dexieDB, data, options?.mappings ?? {}),
    };

    return db as IDB;
}

export default IndexedDB;
